import type { CardfinderSessionType } from '@/shared/api/db/tables';
import { readSession, removeFromSession, writeSession } from '@/shared/lib/sessionStorage';

const SESSION = 'session';

/** Mirror of the funnel answer state; owned by `useLocalAnswerHydration`. */
export const ANSWERS_KEY = 'answers';

/**
 * The backend session this tab's answers belong to.
 *
 * `recovered` carries no id: those answers came from `GET ?status=complete`,
 * which strips all metadata, so there is no write target to record.
 *
 * `attemptedAs` is the identity class the completion write was *issued* under,
 * derived from cookie presence — so it is an intent, not a record of what the
 * server stored. It lets an anonymous run be re-saved once under a real
 * identity without that path looping.
 */
export type LocalFunnelSession =
  | { kind: 'in-progress'; id: string }
  | { kind: 'complete'; id: string; attemptedAs: CardfinderSessionType }
  | { kind: 'recovered' };

export function readLocalSession(): LocalFunnelSession | undefined {
  return readSession<LocalFunnelSession>(SESSION);
}

/** True once the run is finished, so the next edit must start a new one. */
export function shouldRotateLocalSession(session: LocalFunnelSession | undefined): boolean {
  return session?.kind === 'complete' || session?.kind === 'recovered';
}

export function markLocalSessionInProgress(id: string): void {
  writeSession<LocalFunnelSession>(SESSION, { kind: 'in-progress', id });
}

export function markLocalSessionComplete(id: string, attemptedAs: CardfinderSessionType): void {
  writeSession<LocalFunnelSession>(SESSION, { kind: 'complete', id, attemptedAs });
}

/** Records that the on-screen answers came from the caller's saved session. */
export function adoptRecoveredSession(): void {
  writeSession<LocalFunnelSession>(SESSION, { kind: 'recovered' });
}

/**
 * Rotate: drop the backend session but keep the answers, so editing a finished
 * run starts a new session with the funnel still pre-populated.
 */
export function resetLocalSession() {
  removeFromSession(SESSION);
}

/** Discard: abandon the run entirely. Only for paths that also reset answers. */
export function discardFunnelRun() {
  resetLocalSession();
  removeFromSession(ANSWERS_KEY);
}
